// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Site → Procedures. Ported from the `isKb` block of
// `Helios Ops Manager v2.1.dc.html` — its "Knowledge Base" screen, minus its
// seed. The mock authored 12 SOPs by hand and answered questions with a
// client-side keyword match over them (`seedSops` / `askKb`); this reads the
// real SOP Factory register instead.

import React, { useEffect, useState } from 'react';
import { Button, Search } from '@carbon/react';
import { PageHead, EmptyState, Loading } from '../../components/shell/ScreenHelpers';
import SiteTabs from '../../components/shell/SiteTabs';
import SopLibraryGrid from '../../components/sop-library/SopLibraryGrid';
import { useParams } from 'react-router-dom';
import { useUrlState } from '../../hooks/useUrlState';
import { useSopSource } from '../../hooks/useSopSource';
import { listSops } from '../../services/sopFactoryService';
import type { ListSopsFilters } from '../../services/sopFactoryService';
import { useShellStore } from '../../stores/shellStore';
import type { ArtifactSummary } from '../../types';
import './SiteProcedures.css';

const SEARCH_DEBOUNCE_MS = 300;

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; sops: ArtifactSummary[] };

export default function SiteProcedures() {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [{ q }, patchUrl] = useUrlState({ q: '' });
  const openAskHelios = useShellStore(s => s.openAskHelios);
  const { site_id: siteId } = useParams();
  // ADM (site-scoped) while adm-sop-catalog-enabled is on, else the global catalog.
  const { ready: sourceReady, source } = useSopSource(siteId);
  const list = (filters: ListSopsFilters) =>
    source ? listSops(filters, source) : listSops(filters);

  useEffect(() => {
    if (!sourceReady) return;
    let cancelled = false;
    setState({ status: 'loading' });
    list({ artifactType: 'sop', pageSize: 200 })
      .then(page => {
        if (!cancelled) setState({ status: 'ok', sops: page.items });
      })
      .catch(err => {
        if (!cancelled) {
          setState({ status: 'error', message: (err as Error).message ?? 'Request failed' });
        }
      });
    return () => {
      cancelled = true;
    };
    // `list` is rebuilt every render; `source` is what actually changes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceReady, source]);

  const qt = q.trim();
  // Server-search results belong to the source they were read from (the catalog, or ADM for one
  // site): keyed by it as well as by the query, a site change or the flag flipping never shows
  // the previous source's cards, and the next search is not skipped for having "results".
  const sourceKey = source ? source.siteId : '';
  const [server, setServer] = useState<{
    q: string;
    sourceKey: string;
    items: ArtifactSummary[];
  } | null>(null);
  const hasSops = state.status === 'ok' && state.sops.length > 0;
  useEffect(() => {
    if (!qt || !hasSops) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      list({ artifactType: 'sop', q: qt, pageSize: 200 })
        .then(page => page.items ?? [])
        .catch(err => {
          console.warn('SOP search failed; showing local matches only', err);
          return [] as ArtifactSummary[];
        })
        .then(items => {
          if (!cancelled) setServer({ q: qt, sourceKey, items });
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qt, hasSops, source]);
  const serverCurrent = server?.q === qt && server.sourceKey === sourceKey;
  const searching = hasSops && qt !== '' && !serverCurrent;

  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  const sops = state.status === 'ok' ? state.sops : [];
  const local = sops.filter(sop => {
    const haystack = [
      sop.artifact_id,
      sop.title,
      sop.status,
      sop.version,
      ...Object.values(sop.categories ?? {}).filter(v => typeof v === 'string'),
    ]
      .join(' ')
      .toLowerCase();
    return terms.every(t => haystack.includes(t));
  });
  const seen = new Set(local.map(s => s.artifact_id));
  const extra = serverCurrent ? server.items.filter(s => !seen.has(s.artifact_id)) : [];
  const visible = [...local, ...extra];

  return (
    <div className="hs-screen">
      <SiteTabs />
      <div className="hs-screen-body">
        <PageHead title="Procedures" />

        <div className="hs-toolbar">
          <div className="sl-search">
            <Search
              size="md"
              labelText="Search procedures"
              placeholder="Search id, title, category, status…"
              value={q}
              onChange={e => patchUrl({ q: e.target.value })}
            />
          </div>
          {searching && (
            <span className="sl-searching" role="status">
              Searching…
            </span>
          )}
          <Button size="md" className="sl-ask-btn" onClick={() => openAskHelios()}>
            Ask EnergyFluo
          </Button>
        </div>

        {state.status === 'loading' && <Loading label="Loading procedures…" />}

        {state.status === 'error' && (
          <EmptyState
            big="The SOP register could not be reached."
            sub="The SOP Factory catalog did not respond."
          />
        )}

        {state.status === 'ok' && state.sops.length === 0 && (
          <EmptyState
            big="No procedures returned."
            sub="The SOP Factory catalog has no SOP artifacts published yet."
          />
        )}

        {hasSops && qt !== '' && !searching && visible.length === 0 && (
          <EmptyState big={`No procedures match “${qt}”.`} sub="Try a different keyword." />
        )}

        {visible.length > 0 && <SopLibraryGrid sops={visible} siteId={source?.siteId} />}
      </div>
    </div>
  );
}
